<template>
  <InputLabel
    :label="label"
    :tooltip="tooltip"
    :is-error="isError"
    :warning="warning"
    :class="props.class"
  >
    <div ref="containerRef" class="relative">
      <input
        ref="inputRef"
        v-model="displayValue"
        type="text"
        inputmode="decimal"
        :placeholder="placeholder ?? defaultPlaceholder"
        :disabled="disabled"
        :aria-expanded="showPicker"
        aria-haspopup="listbox"
        :class="inputCva({ size, variant })"
        :data-error="isError ? 'true' : undefined"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handleInput"
        @keydown.enter.prevent="commitDisplayValue"
        @keydown.escape.prevent="handleEscape"
        @keydown="handleKeydown"
      />
      <div
        v-if="showPicker"
        class="absolute left-0 top-full z-20 mt-1 flex overflow-hidden rounded-md border border-input bg-background shadow-md"
        role="listbox"
        @mousedown.prevent
      >
        <div class="flex w-[9rem] flex-col border-r border-input p-1.5">
          <div class="flex items-center justify-between gap-1 pb-1.5">
            <button
              type="button"
              :class="hourStepperButtonClass"
              :disabled="pickerHours <= 0"
              :aria-label="t('decreaseHours')"
              @click="decrementHours"
            >
              −
            </button>
            <span class="whitespace-nowrap text-xs font-semibold text-obsidian-700">
              {{ t('hours') }}
            </span>
            <button
              type="button"
              :class="hourStepperButtonClass"
              :disabled="pickerHours >= maxHours"
              :aria-label="t('increaseHours')"
              @click="incrementHours"
            >
              +
            </button>
          </div>
          <ul ref="hoursListRef" class="h-36 w-full overflow-y-auto overscroll-contain">
            <li v-for="hour in hourOptions" :key="hour">
              <button
                type="button"
                class="block w-full px-1 py-1 text-center text-xs tabular-nums hover:bg-obsidian-50"
                :class="{ 'bg-hazel-100 font-medium': hour === pickerHours }"
                :data-selected="hour === pickerHours ? 'true' : undefined"
                @click="updateDuration(hour, pickerMinutes)"
              >
                {{ hour }}
              </button>
            </li>
          </ul>
        </div>
        <div class="flex w-[9rem] flex-col p-1.5">
          <div class="flex items-center justify-between gap-1 pb-1.5">
            <button
              type="button"
              :class="minuteStepperButtonClass"
              :disabled="pickerHours <= 0 && pickerMinutes <= 0"
              :aria-label="t('decreaseMinutes')"
              @click="decrementMinutes"
            >
              −5
            </button>
            <span class="whitespace-nowrap text-xs font-semibold text-obsidian-700">
              {{ t('minutes') }}
            </span>
            <button
              type="button"
              :class="minuteStepperButtonClass"
              :disabled="pickerHours >= maxHours && pickerMinutes >= 59"
              :aria-label="t('increaseMinutes')"
              @click="incrementMinutes"
            >
              +5
            </button>
          </div>
          <ul ref="minutesListRef" class="h-36 w-full overflow-y-auto overscroll-contain">
            <li v-for="minute in minuteOptions" :key="minute">
              <button
                type="button"
                class="block w-full px-1 py-1 text-center text-xs tabular-nums hover:bg-obsidian-50"
                :class="{ 'bg-hazel-100 font-medium': minute === pickerMinutes }"
                :data-selected="minute === pickerMinutes ? 'true' : undefined"
                @click="updateDuration(pickerHours, minute)"
              >
                {{ String(minute).padStart(2, '0') }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <InputHint :hint="hint" :is-error="isError" />
  </InputLabel>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import InputHint from './InputHint.vue';
import InputLabel from './InputLabel.vue';
import { input as inputCva } from '@carvia/theme/lib/cva/input';
import {
  formatDurationMinutes,
  getDecimalSeparator,
  getDurationInputSegment,
  parseDurationInput,
  splitDurationMinutes,
} from '@/utils/duration-input';

const props = withDefaults(
  defineProps<{
    modelValue?: number;
    label?: string;
    placeholder?: string;
    hint?: string;
    isError?: boolean;
    warning?: string;
    tooltip?: string;
    disabled?: boolean;
    class?: string;
    maxHours?: number;
    variant?: 'default' | 'ghost';
    size?: 'sm' | 'default' | 'lg';
  }>(),
  {
    modelValue: 0,
    maxHours: 72,
    size: 'sm',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void;
}>();

const { t, locale } = useI18n();

const containerRef = ref<HTMLElement>();
const inputRef = ref<HTMLInputElement>();
const hoursListRef = ref<HTMLElement>();
const minutesListRef = ref<HTMLElement>();

const showPicker = ref(false);
const isFocused = ref(false);
const displayValue = ref(formatDurationMinutes(props.modelValue));

const defaultPlaceholder = computed(() => {
  const decimalSeparator = locale.value === 'de' ? ',' : '.';
  return `0:00 / 1${decimalSeparator}5`;
});

const hourOptions = computed(() => Array.from({ length: props.maxHours + 1 }, (_, index) => index));
const minuteOptions = computed(() => Array.from({ length: 60 }, (_, index) => index));

const pickerHours = computed(() => splitDurationMinutes(props.modelValue).hours);
const pickerMinutes = computed(() => splitDurationMinutes(props.modelValue).remainingMinutes);

const scrollPickerToSelection = async () => {
  await nextTick();
  hoursListRef.value?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'center' });
  minutesListRef.value
    ?.querySelector('[data-selected="true"]')
    ?.scrollIntoView({ block: 'center' });
};

watch(
  () => props.modelValue,
  (minutes) => {
    if (isFocused.value) {
      if (showPicker.value) {
        scrollPickerToSelection();
      }
      return;
    }
    displayValue.value = formatDurationMinutes(minutes);
  },
);

watch(showPicker, (open) => {
  if (open) {
    scrollPickerToSelection();
  }
});

onClickOutside(containerRef, () => {
  if (!showPicker.value) {
    return;
  }
  showPicker.value = false;
  commitDisplayValue();
});

const emitMinutes = (minutes: number) => {
  emit('update:modelValue', minutes);
};

const commitDisplayValue = () => {
  const parsed = parseDurationInput(displayValue.value, locale.value);
  if (!parsed || 'error' in parsed) {
    displayValue.value = formatDurationMinutes(props.modelValue);
    return;
  }

  emitMinutes(parsed.minutes);
  displayValue.value = formatDurationMinutes(parsed.minutes);
};

const handleFocus = () => {
  if (props.disabled) {
    return;
  }
  isFocused.value = true;
  showPicker.value = true;
};

const handleBlur = () => {
  isFocused.value = false;
  showPicker.value = false;
  commitDisplayValue();
};

const handleInput = () => {
  const parsed = parseDurationInput(displayValue.value, locale.value);
  if (parsed && !('error' in parsed)) {
    emitMinutes(parsed.minutes);
  }
};

const updateDuration = (hours: number, minutes: number) => {
  const clampedHours = Math.max(0, Math.min(props.maxHours, hours));
  const clampedMinutes = Math.max(0, Math.min(59, minutes));
  const totalMinutes = clampedHours * 60 + clampedMinutes;

  emitMinutes(totalMinutes);
  displayValue.value = formatDurationMinutes(totalMinutes);
  scrollPickerToSelection();
};

const MINUTE_STEP = 5;

const stepperButtonBaseClass =
  'inline-flex h-7 shrink-0 items-center justify-center rounded-md border border-input bg-background p-0 text-xs font-semibold text-obsidian-700 hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40';

const hourStepperButtonClass = `${stepperButtonBaseClass} w-7`;
const minuteStepperButtonClass = `${stepperButtonBaseClass} w-8`;

const incrementHours = () => updateDuration(pickerHours.value + 1, pickerMinutes.value);
const decrementHours = () => updateDuration(pickerHours.value - 1, pickerMinutes.value);

const incrementMinutes = () => {
  const maxTotal = props.maxHours * 60 + 59;
  const { hours, remainingMinutes } = splitDurationMinutes(
    Math.min(props.modelValue + MINUTE_STEP, maxTotal),
  );
  updateDuration(hours, remainingMinutes);
};

const decrementMinutes = () => {
  const { hours, remainingMinutes } = splitDurationMinutes(
    Math.max(0, props.modelValue - MINUTE_STEP),
  );
  updateDuration(hours, remainingMinutes);
};

const getCurrentMinutes = () => {
  const parsed = parseDurationInput(displayValue.value, locale.value);
  if (parsed && !('error' in parsed)) {
    return parsed.minutes;
  }
  return props.modelValue;
};

const restoreCursorForSegment = (segment: 'hours' | 'minutes', formattedValue: string) => {
  const input = inputRef.value;
  if (!input) {
    return;
  }

  const colonIndex = formattedValue.indexOf(':');
  if (colonIndex !== -1) {
    if (segment === 'hours') {
      input.setSelectionRange(colonIndex, colonIndex);
      return;
    }
    input.setSelectionRange(colonIndex + 1, formattedValue.length);
    return;
  }

  const decimalSeparator = getDecimalSeparator(locale.value);
  const decimalIndices = [
    formattedValue.indexOf('.'),
    formattedValue.indexOf(decimalSeparator),
  ].filter((index) => index !== -1);
  if (decimalIndices.length > 0) {
    const decimalIndex = Math.min(...decimalIndices);
    if (segment === 'hours') {
      input.setSelectionRange(decimalIndex, decimalIndex);
      return;
    }
    input.setSelectionRange(formattedValue.length, formattedValue.length);
    return;
  }

  input.setSelectionRange(formattedValue.length, formattedValue.length);
};

const handleArrowKey = async (direction: 'up' | 'down') => {
  if (props.disabled) {
    return;
  }

  const input = inputRef.value;
  if (!input) {
    return;
  }

  const cursorPosition = input.selectionStart ?? displayValue.value.length;
  const segment = getDurationInputSegment(displayValue.value, cursorPosition, locale.value);
  const currentMinutes = getCurrentMinutes();
  const maxTotal = props.maxHours * 60 + 59;
  const delta =
    segment === 'hours'
      ? direction === 'up'
        ? 60
        : -60
      : direction === 'up'
        ? MINUTE_STEP
        : -MINUTE_STEP;
  const nextMinutes = Math.max(0, Math.min(maxTotal, currentMinutes + delta));
  const { hours, remainingMinutes } = splitDurationMinutes(nextMinutes);

  updateDuration(hours, remainingMinutes);

  await nextTick();
  restoreCursorForSegment(segment, displayValue.value);
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowUp') {
    event.preventDefault();
    handleArrowKey('up');
    return;
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    handleArrowKey('down');
  }
};

const handleEscape = () => {
  showPicker.value = false;
  displayValue.value = formatDurationMinutes(props.modelValue);
  inputRef.value?.blur();
};

defineExpose({
  blur: () => inputRef.value?.blur(),
  input: inputRef,
});
</script>

<i18n lang="json">
{
  "en": {
    "hours": "Hours",
    "minutes": "Minutes",
    "increaseHours": "Increase hours",
    "decreaseHours": "Decrease hours",
    "increaseMinutes": "Increase minutes by 5",
    "decreaseMinutes": "Decrease minutes by 5"
  },
  "de": {
    "hours": "Stunden",
    "minutes": "Minuten",
    "increaseHours": "Stunden erhöhen",
    "decreaseHours": "Stunden verringern",
    "increaseMinutes": "Minuten um 5 erhöhen",
    "decreaseMinutes": "Minuten um 5 verringern"
  },
  "es": {
    "hours": "Horas",
    "minutes": "Minutos",
    "increaseHours": "Aumentar horas",
    "decreaseHours": "Reducir horas",
    "increaseMinutes": "Aumentar minutos en 5",
    "decreaseMinutes": "Reducir minutos en 5"
  }
}
</i18n>
